// NotificationBell.jsx — v119.151 In-app notification bell + slide-over.
// - Poll unread count every 30s.
// - Click bell → open Sheet drawer showing grouped notifications (Today / Yesterday / Older).
// - Click a notification → mark read + optionally jump to a tab (link field).
import React, { useCallback, useEffect, useState } from "react";
import { Bell, Check, X } from "lucide-react";
import { api } from "./lib/api";
import { Portal } from "./components/Portal";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";
import { formatKSADate, ksaAddDays, ksaToday } from "./lib/ksaTime";

const TYPE_COLORS = {
  task:    { bg: "#dbeafe", fg: "#1e40af" },
  meeting: { bg: "#fef3c7", fg: "#92400e" },
  alert:   { bg: "#fee2e2", fg: "#b91c1c" },
  message: { bg: "#dcfce7", fg: "#15803d" },
  system:  { bg: "#f3e8ff", fg: "#7e22ce" },
};

const TYPE_LABELS_AR = {
  task: "مهمة", meeting: "اجتماع", alert: "تنبيه",
  message: "رسالة", system: "نظام",
  quality_report: "جودة القهوة", secret_customer: "عميل سرّي",
  purchase_request: "طلب شراء", idea_bank: "بنك الأفكار",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "الآن";
  if (mins < 60) return `${mins} دقيقة`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `منذ ${hrs} ساعة`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `منذ ${days} يوم`;
  return formatKSADate(d, { month: "short", day: "numeric" });
};

const bucketOf = (iso) => {
  if (!iso) return "older";
  const day = ksaToday(new Date(iso));
  const today = ksaToday();
  if (day === today) return "today";
  if (day === ksaAddDays(today, -1)) return "yesterday";
  return "older";
};

// v119.206 — Smart routing aliases: notification `link` values → tab ids.
// v119.241 — Bilingual mapping (Arabic + English kinds) per CEO directive.
// Phase 12 Cutover 2026-07-25: legacy customer tabs (retm_cust, expected,
// lab, surveys) removed from sidebar; notifs pointing to them are silently
// redirected to `customer_hub` and the correct sub-tab is stashed in
// localStorage (see HUB_SUB_TAB_MAP + handleClick).
const LINK_MAP = {
  requests: "reqs", request: "reqs",
  marketing: "marketing_hub", ideas: "marketing_hub", idea_bank: "marketing_hub",
  campaigns: "marketing_hub", mk_hub: "marketing_hub",
  gantt: "mk_gantt", settings: "st", quality: "quality", training: "training",
  camp_sched: "camp_sched", whatsapp: "whatsapp", pos: "pos", hr: "employees",
  // Phase 12 — legacy customer tabs → unified hub.
  retm_cust: "customer_hub",
  expected:  "customer_hub",
  lab:       "customer_hub",
  surveys:   "customer_hub",
  send_caps: "customer_hub",
  // bilingual aliases
  "عميل_سري": "mystery", secret_customer: "mystery", mystery: "mystery",
  "طلب_شراء": "reqs", purchase_request: "reqs",
  "بنك_الأفكار": "marketing_hub",
};

// Phase 12 — when a legacy customer notif is remapped to customer_hub,
// this map tells CustomerHub which sub-tab to open.
const HUB_SUB_TAB_MAP = {
  retm_cust: "directory",
  expected:  "expected",
  lab:       "lab",
  surveys:   "comms",
  send_caps: "cap_approvals",
};

const resolveLink = (link) => {
  if (!link) return null;
  let t = String(link);
  if (t.startsWith("/") || t.startsWith("?")) {
    try { t = new URL(t, window.location.origin).searchParams.get("tab") || "home"; }
    catch { return null; }
  }
  return LINK_MAP[t] || t;
};

// Older notifications have no `link` — route by their type/kind instead.
const KIND_FALLBACK = {
  request_workflow: "reqs", request: "reqs",
  ceo_box: "ceo", idea_bank: "marketing_hub", idea_honor: "marketing_hub",
  branch_report: "home", mystery_responsibility: "mystery",
  quality: "quality", training: "training",
  campaign_scheduler: "camp_sched",
  // v119.241 bilingual aliases
  "عميل_سري": "mystery", secret_customer: "mystery",
  "طلب_شراء": "reqs", purchase_request: "reqs",
  "بنك_الأفكار": "marketing_hub",
  quality_report: "quality",
};

// v119.241 (CEO directive) — returns NULL when the notification is unknown:
// the user STAYS on the current page instead of being yanked to home.
const resolveTarget = (n) =>
  resolveLink(n.link) || KIND_FALLBACK[n.type] || KIND_FALLBACK[n.kind] || null;

export default function NotificationBell({ onNav }) {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const [r, m] = await Promise.allSettled([
        api.get("/notifications/unread-count"),
        api.get("/notifications/my"),
      ]);
      const sys = r.status === "fulfilled" ? (r.value.data?.unread || 0) : 0;
      const adm = m.status === "fulfilled" ? (m.value.data?.unread || 0) : 0;
      setUnread(sys + adm);
    } catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      // v119.201 — the bell is the SINGLE notification center: merge system
      // notifications with admin-sent inbox messages (formerly on HomePage).
      const [r, m] = await Promise.allSettled([
        api.get("/notifications?limit=50"),
        api.get("/notifications/my"),
      ]);
      const sys = r.status === "fulfilled" ? (r.value.data?.items || []) : [];
      const sysUnread = r.status === "fulfilled" ? (r.value.data?.unread_count || 0) : 0;
      const myData = m.status === "fulfilled" ? (m.value.data || {}) : {};
      const my = (myData.items || []).map((n) => ({
        ...n, _admin: true, type: n.source || "message", icon: "📨",
        body: n.body ? `${n.body}${n.from_username ? `\nمن: ${n.from_username}` : ""}` : "",
      }));
      const merged = [...sys, ...my].sort(
        (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setItems(merged);
      setUnread(sysUnread + (myData.unread || 0));
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => {
    const t = setInterval(loadCount, 30_000);
    return () => clearInterval(t);
  }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  // v119.205 — PWA app-icon badge (iOS/Android installed app).
  useEffect(() => {
    try {
      if ("setAppBadge" in navigator) {
        if (unread > 0) navigator.setAppBadge(unread);
        else navigator.clearAppBadge();
      }
    } catch (_e) { /* unsupported browser */ }
  }, [unread]);

  // v119.152 — allow external triggers (e.g. mobile bottom bar) to open the drawer.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("tw-open-notif", handler);
    return () => window.removeEventListener("tw-open-notif", handler);
  }, []);

  const markRead = async (n) => {
    try {
      if (n._admin) {
        await api.post("/notifications/read", { ids: [n.id] });
      } else {
        await api.post(`/notifications/${n.id}/read`);
      }
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await Promise.allSettled([
        api.post("/notifications/read-all"),
        api.post("/notifications/read", { ids: [] }),
      ]);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n);
    // v119.241 — navigate ONLY when the target is known; otherwise stay put
    // (mark as read + close the panel, no jump to home).
    const target = resolveTarget(n);
    if (target) {
      // Phase 12 Cutover 2026-07-25: notifs pointing to a legacy customer
      // tab (expected/lab/retm_cust/surveys) get remapped to customer_hub;
      // stash the intended sub-tab so CustomerHub opens on the right pane.
      const rawLink = String(n.link || "");
      if (target === "customer_hub" && HUB_SUB_TAB_MAP[rawLink]) {
        try { localStorage.setItem("hub.default_sub_tab", HUB_SUB_TAB_MAP[rawLink]); }
        catch (_e) { /* storage disabled — silent */ }
      }
      if (typeof onNav === "function") onNav(target);
      try {
        const u = new URL(window.location.href);
        u.searchParams.set("tab", target);
        u.searchParams.delete("sub");
        window.history.replaceState(null, "", u.toString());
      } catch (_e) { /* noop */ }
      // PROD-FIX-2 (بند 1) — «طلب إيقاف واتساب» يفتح طلب العميل نفسه في
      // قائمة المعلّقين (تبويب الاستبعاد)، لا صفحة الإعدادات العامة.
      if (n.kind === "product_cost_missing" && n.entity_id) {
        try {
          localStorage.setItem("tw_pos_sub", "settings");
          localStorage.setItem("costs.focus_item", n.entity_id);
        } catch (_e) { /* storage disabled — silent */ }
        window.setTimeout(() => window.dispatchEvent(
          new CustomEvent("tw-pos-sub", { detail: { sub: "settings" } })), 250);
        window.setTimeout(() => window.dispatchEvent(
          new CustomEvent("costs-focus-item", { detail: { key: n.entity_id } })), 700);
      }
      if (n.kind === "wa_optout_pending" && n.entity_id) {
        window.setTimeout(() => window.dispatchEvent(
          new CustomEvent("wa-goto-tab", { detail: { tab: "optout" } })), 250);
        window.setTimeout(() => window.dispatchEvent(
          new CustomEvent("wa-optout-focus-phone", { detail: { phone: n.entity_id } })), 500);
      }
    }
    setOpen(false);
  };

  const groups = { today: [], yesterday: [], older: [] };
  items.forEach((n) => { groups[bucketOf(n.created_at)].push(n); });

  return (
    <>
      {/* Bell trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="notif-bell"
 
        className="relative rounded-full p-2 hover:bg-black/5 transition-colors"
        style={{ color: "#5c3a1e" }}
       aria-label="الإشعارات">
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
            style={{
              background: "#c92a2a",
              minWidth: 18, height: 18, padding: "0 4px",
            }}
            data-testid="notif-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Slide-over drawer — الجولة الختامية (2026-10، البند 4): يُركَّب
          الآن عبر Portal (مستوى notification) بدل div محلي بـz-50 حرفي —
          كان يقع تحت .mob-nav/.hdr الثابتين على iPad (z-index 55/100). */}
      {open && (
        <Portal level="notification">
          <div data-testid="notif-drawer" style={{ position: "fixed", inset: 0 }}>
            <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
            <div
              className="absolute bottom-0 w-full sm:w-[400px] bg-white shadow-2xl overflow-y-auto"
              style={{
                insetInlineStart: 0, direction: "rtl",
                top: "env(safe-area-inset-top, 0px)",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              {/* Header */}
              <div
                className="sticky top-0 p-4 border-b flex items-center justify-between"
                style={{
                  zIndex: zVar('base'),
                  borderColor: "#e8dcd0",
                  background: "linear-gradient(135deg,#4a2c1a 0%,#6b3f26 100%)",
                }}
              >
                <div>
                  <h2 className="text-white text-lg font-bold">🔔 الإشعارات</h2>
                  <div className="text-white/70 text-xs mt-0.5">
                    {unread > 0 ? `لديك ${unread} إشعار جديد` : "لا إشعارات جديدة"}
                  </div>
                </div>
                <div className="flex gap-1 items-center">
                  {unread > 0 && (
                    <button
                      onClick={markAllRead}
                      data-testid="notif-mark-all"
                      className="text-white/90 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10 flex items-center gap-1"
                      style={{ minHeight: 44 }}
                    >
                      <Check size={14} /> قراءة الكل
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="notif-close"
                    className="text-white/90 hover:text-white rounded hover:bg-white/10 flex items-center justify-center"
                    style={{ minWidth: 44, minHeight: 44 }}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* List */}
            <div className="p-3 space-y-4">
              {loading && (
                <div className="text-center opacity-60 py-10 text-sm">جاري التحميل…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="text-center opacity-60 py-16">
                  <div className="text-4xl mb-2">📭</div>
                  <div className="text-sm">صندوقك فارغ! لا إشعارات حالياً.</div>
                </div>
              )}
              {!loading && ["today", "yesterday", "older"].map((g) => (
                groups[g].length > 0 && (
                  <div key={g}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-1"
                         style={{ color: "#8c7362" }}>
                      {g === "today" ? "اليوم" : g === "yesterday" ? "الأمس" : "أقدم"}
                    </div>
                    <div className="space-y-2">
                      {groups[g].map((n, i) => {
                        const c = TYPE_COLORS[n.type] || TYPE_COLORS.system;
                        const nk = n.id || `${g}-${n.type || "n"}-${i}`;
                        return (
                          <button
                            key={nk}
                            onClick={() => handleClick(n)}
                            data-testid={`notif-item-${nk}`}
                            className={`w-full text-right p-3 rounded-xl border transition-all hover:shadow-md ${
                              !n.read ? "bg-amber-50/50" : "bg-white"
                            }`}
                            style={{ borderColor: !n.read ? "#d4a574" : "#e8dcd0" }}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0"
                                style={{ background: c.bg, color: c.fg }}
                              >
                                {n.icon || "🔔"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-2">
                                  <div className="text-sm font-semibold truncate"
                                       style={{ color: "#5c3a1e" }}>{n.title}</div>
                                  {!n.read && (
                                    <span
                                      className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                                      style={{ background: "#c92a2a" }}
                                    />
                                  )}
                                </div>
                                {n.body && (
                                  <div className="text-[12px] opacity-70 mt-0.5 line-clamp-2"
                                       style={{ color: "#5c3a1e" }}>{n.body}</div>
                                )}
                                <div className="flex justify-between items-center mt-1.5">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded"
                                        style={{ background: c.bg, color: c.fg }}>
                                    {TYPE_LABELS_AR[n.type] || n.type}
                                  </span>
                                  <span className="text-[10px] opacity-60">{timeAgo(n.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
        </Portal>
      )}
    </>
  );
}
